import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../Global/topbar";
import CachedIcon from "@mui/icons-material/Cached";
import { RiFileExcel2Line } from "react-icons/ri";
import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { getConsultantsByBalance } from "../../services/api/consultantsPayout";
import { exportToExcel } from "../Global/exportToExcel";
import WithdrawDialog from "./WithdrawDialog";
import SettlementHistoryDialog from "./SettlementHistoryDialog";
import WithdrawalRequests from "./WithdrawalRequests";
import "../Audio Type/audio.css";
import "./consultantsPayout.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ConsultantsPayout() {
  const [view, setView] = useState("payout"); // "payout" | "requests"
  const [minBalance, setMinBalance] = useState(0);
  const [search, setSearch] = useState("");
  const [loader, setLoader] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rows, setRows] = useState([]);
  const [reload, setReload] = useState(true);

  // Parent owns only the targeting state. Form fields live inside WithdrawDialog
  // so typing doesn't re-render the consultants table on every keystroke.
  const [withdrawTarget, setWithdrawTarget] = useState(null);

  const openWithdraw = useCallback((consultant) => {
    setWithdrawTarget(consultant);
  }, []);
  const closeWithdraw = useCallback(() => {
    setWithdrawTarget(null);
  }, []);
  const handleWithdrawSuccess = useCallback(() => {
    setReload((p) => !p);
  }, []);

  const [historyTarget, setHistoryTarget] = useState(null);
  const openHistory = useCallback((consultant) => setHistoryTarget(consultant), []);
  const closeHistory = useCallback(() => setHistoryTarget(null), []);

  const fetchList = async () => {
    setLoader(true);
    setNotFound(false);
    try {
      const response = await getConsultantsByBalance({
        min_balance: Number(minBalance) || 0,
      });
      if (response?.data?.status === "success") {
        const list = response?.data?.data?.data || [];
        setRows(list);
        if (list.length === 0) setNotFound(true);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [reload, minBalance]);

  // Client-side search across name / email / phone / bank fields. Cheap on the
  // ~few-hundred-row payout list — no need for server-side search yet.
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      [r.name, r.email, r.phone_number, r.account_number, r.ifsc, r.bank_name, r.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.total_balance += Number(r.balance || 0);
        acc.count += 1;
        if (r.account_number) acc.with_bank += 1;
        return acc;
      },
      { total_balance: 0, count: 0, with_bank: 0 },
    );
  }, [filteredRows]);

  return (
    <div className="audio-main">
      <div className="audio-user-top">
        <Topbar />
        <div className="top-mid-audio">
          <h1>Consultants Payout</h1>
          <Link to="/admin/consultants_payout">Consultants Payout</Link> &#62;
        </div>

        <div className="payout-toolbar" style={{ gap: 8 }}>
          <Button
            variant={view === "payout" ? "contained" : "outlined"}
            size="small"
            onClick={() => setView("payout")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Consultants Payout
          </Button>
          <Button
            variant={view === "requests" ? "contained" : "outlined"}
            size="small"
            onClick={() => setView("requests")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Withdrawal Requests
          </Button>
        </div>

        {view === "requests" ? (
          <WithdrawalRequests />
        ) : (
        <>
        <div className="payout-toolbar">
          <div className="payout-field">
            <label>Min Balance ₹</label>
            <TextField
              size="small"
              type="number"
              value={minBalance}
              onChange={(e) => setMinBalance(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: 120 }}
            />
          </div>
          <div className="payout-field">
            <label>Search</label>
            <TextField
              size="small"
              placeholder="Name / email / phone / bank…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 280 }}
            />
          </div>
          <button
            className="payout-reload"
            onClick={() => setReload((p) => !p)}
            title="Reload"
          >
            <CachedIcon />
          </button>
          <button
            className="payout-download"
            disabled={!filteredRows.length}
            onClick={() => {
              exportToExcel(
                filteredRows,
                [
                  { header: "Consultant ID", key: "id" },
                  { header: "Name", key: "name" },
                  { header: "Email", key: "email" },
                  { header: "Phone", key: "phone_number" },
                  { header: "Balance", key: "balance" },
                  { header: "Approve Status", key: "approve_status" },
                  { header: "Account Holder", key: "bank_account_details.account_holder_name" },
                  { header: "Bank Name", key: "bank_name" },
                  { header: "IFSC", key: "ifsc" },
                  { header: "Account Number", key: "account_number" },
                  { header: "Account Type", key: "account_type" },
                  { header: "PAN", key: "pan_card" },
                  { header: "Last Settled", key: "last_settled_amount" },
                ],
                `consultants-payout-${new Date().toISOString().slice(0, 10)}.xlsx`,
              );
            }}
            title={filteredRows.length ? "Download as Excel" : "No data"}
          >
            <RiFileExcel2Line style={{ fontSize: "1.1rem" }} />
            <span>Download</span>
          </button>
        </div>

        {!loader && !notFound && filteredRows.length > 0 ? (
          <div className="payout-summary">
            <div className="payout-tile">
              <div className="tile-label">Consultants</div>
              <div className="tile-value">{totals.count}</div>
            </div>
            <div className="payout-tile">
              <div className="tile-label">Total Payable</div>
              <div className="tile-value payable">{inr(totals.total_balance)}</div>
            </div>
            <div className="payout-tile">
              <div className="tile-label">With Bank Details</div>
              <div className="tile-value">
                {totals.with_bank} / {totals.count}
              </div>
            </div>
          </div>
        ) : null}

        <WithdrawDialog
          open={!!withdrawTarget}
          consultant={withdrawTarget}
          onClose={closeWithdraw}
          onSuccess={handleWithdrawSuccess}
        />

        <SettlementHistoryDialog
          open={!!historyTarget}
          consultant={historyTarget}
          onClose={closeHistory}
        />

        <div className="payout-table-wrap">
          {loader ? (
            <div className="payout-loader">
              <CircularProgress size={28} />
            </div>
          ) : notFound || filteredRows.length === 0 ? (
            <div className="payout-empty">
              No consultants found with balance &gt; ₹{minBalance}.
            </div>
          ) : (
            <TableContainer component={Paper} className="payout-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Consultant</TableCell>
                    <TableCell>Email / Phone</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>IFSC</TableCell>
                    <TableCell>PAN</TableCell>
                    <TableCell align="right">Last Settled</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((r, i) => (
                    <TableRow key={r.id || i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <div className="payout-party">
                          <Avatar
                            src={r.photo || undefined}
                            alt={r.name || "?"}
                            sx={{ width: 32, height: 32 }}
                          >
                            {(r.name || "?").slice(0, 1).toUpperCase()}
                          </Avatar>
                          <div>
                            <div className="payout-party-name">{r.name || "-"}</div>
                            <code className="payout-cid">{r.id}</code>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="payout-contact">
                          <div>{r.email || "-"}</div>
                          <div className="payout-phone">{r.phone_number || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <span className="payout-balance">{inr(r.balance)}</span>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.approve_status || "-"}
                          size="small"
                          color={
                            r.approve_status === "Approved" ? "success" : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{r.bank_name || "-"}</TableCell>
                      <TableCell>
                        <code className="payout-cid">{r.account_number || "-"}</code>
                      </TableCell>
                      <TableCell>
                        <code className="payout-cid">{r.ifsc || "-"}</code>
                      </TableCell>
                      <TableCell>
                        <code className="payout-cid">{r.pan_card || "-"}</code>
                      </TableCell>
                      <TableCell align="right">
                        {inr(r.last_settled_amount)}
                      </TableCell>
                      <TableCell align="center">
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            onClick={() => openWithdraw(r)}
                            disabled={Number(r.balance || 0) <= 0}
                            sx={{ textTransform: "none", fontWeight: 600 }}>
                            Withdraw
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => openHistory(r)}
                            sx={{ textTransform: "none" }}>
                            History
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
